import { useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';

import { Button, Callout, Heading, Input, Paragraph, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

export default function Azure() {
	const [step, setStep] = useState(0);
	const [replace, setReplace] = useState(false);
	const [url, setURL] = useState('');

	const [create] = graphql.useCreateIdentityProviderMutation();
	const [update] = graphql.useUpdateIdentityProviderMutation();

	const query = graphql.useSelfServiceIdentityProvidersQuery();

	async function onMetadataURL() {
		const existing = query.data?.identityProviders[0];

		if (existing) {
			await update({
				variables: {
					input: { identityProviderID: existing.id, metadataURL: url },
				},
			});
		} else {
			await create({ variables: { input: { metadataURL: url } } });
		}

		await query.refetch();
	}

	useEffect(() => {
		if (!query.data || query.data.identityProviders.length === 0) {
			return;
		}

		setStep(3);
	}, [query.data]);

	if (!query.data) {
		return <Spinner />;
	}

	return (
		<>
			<Box mb={3}>
				<Heading>2. Maak een nieuwe SAML-koppeling aan in Azure.</Heading>
				<Box ml={3}>
					<Paragraph>
						<ol>
							<li>Open het Azure admin panel.</li>
							<li>
								Ga in het menu links naar <strong>enterprise applications</strong>.
							</li>
							<li>
								Klik op <strong>New application</strong>.
							</li>
							<li>
								Selecteer <strong>Non-galery application</strong> en klik op <strong>Add</strong>.
							</li>
							<li>
								Ga vervolgens naar stap 2 <strong>Sign-in</strong>.
							</li>
							<li>
								Kies in dit scherm voor <strong>SAML</strong>.
							</li>
							<li>
								Klik nu op <strong>Upload metadata XML</strong>.
							</li>
						</ol>
					</Paragraph>
				</Box>
				{step === 0 && (
					<Flex>
						<Button title="Volgende" onClick={() => setStep((step) => step + 1)} />
					</Flex>
				)}
			</Box>
			{step >= 1 && (
				<Box mb={3}>
					<Heading>3. Upload onze metadata XML naar Azure</Heading>
					<Paragraph>
						In Azure wordt er gevraagd om een <strong>metadata bestand</strong> te uploaden. Er zijn
						dan twee mogelijkheden:
						<Box ml={3}>
							<ol>
								<li>
									Vul de volgende URL in het vak van de bestandsnaam in:
									<Flex alignItems="center" mt={2}>
										<Box flexGrow={1} flexShrink={1} mr={2}>
											<Input
												value={query.data.serviceProvider.metadataURL}
												onChange={() => {}}
												disabled
											/>
										</Box>
										<Button
											title="Kopiëren"
											onClick={async () => {
												await navigator.clipboard.writeText(
													query.data!.serviceProvider.metadataURL,
												);
											}}
										/>
									</Flex>
								</li>
								<li>
									<strong>of</strong> download het onderstaande bestand en upload dat vervolgens in
									Azure:
									<Flex mt={2} alignItems="center">
										<a
											href={query.data.serviceProvider.metadataURL}
											target="_blank"
											download="metadata.xml"
											rel="noreferrer"
										>
											<Button title="Downloaden" />
										</a>
									</Flex>
								</li>
							</ol>
						</Box>
						Klik hierna op <strong>Save</strong>.
					</Paragraph>

					{step === 1 && (
						<Flex mt={2}>
							<Button title="Volgende" onClick={() => setStep((step) => step + 1)} />
						</Flex>
					)}
				</Box>
			)}
			{step >= 2 && (
				<Box mb={3}>
					<Heading>4. Geef jullie metadata XML terug aan ons</Heading>
					<Paragraph>
						Kopieer de URL uit het grijze <strong>App Federation Metadata Url</strong> vakje, en
						plak die hieronder terug in Treams:
					</Paragraph>
					{query.data.identityProviders.length > 0 && !replace ? (
						<Callout
							mt={2}
							color="green"
							title="Geupload!"
							description="Als je nieuwe metadata aan ons wil doorgeven, klik je op de knop hiernaast. Meestal is dat echter niet nodig."
						>
							<Box>
								<Button type="secondary" title="Vervangen" onClick={() => setReplace(true)} />
							</Box>
						</Callout>
					) : (
						<Flex alignItems="center" mt={2}>
							<Box flexGrow={1} flexShrink={1} mr={2}>
								<Input
									placeholder="https://login.microsoftonline.com/"
									value={url}
									onChange={setURL}
								/>
							</Box>
							<Button title="Volgende" onClick={onMetadataURL} />
						</Flex>
					)}
				</Box>
			)}
			{step >= 3 && (
				<Box mb={3}>
					<Heading>
						5. Wijs in Azure een gebruikersgroep toe om toegang te geven aan Treams.
					</Heading>
					<Paragraph>
						Belangrijk om te vermelden: gebruikers zonder Treams-licentie worden niet gefactureerd.
						Het is dus vaak het makkelijkst om iedereen in deze Azure groep te zetten: wij checken
						altijd zelf nog of de gebruiker daadwerkelijk een Treams account heeft.
					</Paragraph>
					<Flex mt={2}>
						<Button title="Volgende" onClick={() => setStep(4)} />
					</Flex>
					{step === 4 && (
						<Box mt={2}>
							<Callout
								color="blue"
								title="Gelukt!"
								description="Het werk zit er al weer op! Nu kan iedereen gebruik maken van automatisch inloggen met Treams."
							/>
						</Box>
					)}
				</Box>
			)}
		</>
	);
}
